import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AvatarIcon, CountryFlag } from "@/lib/identity";
import { PremiumBadge } from "@/components/PremiumBadge";
import { toast } from "sonner";
import { MagnifyingGlass, Crown, Prohibit, ArrowCounterClockwise, CheckCircle, X, Trash, Lightning } from "@phosphor-icons/react";

export default function Players() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/players", { params: { q, country: country || undefined, status: status || undefined } });
      setRows(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [country, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const act = async (path, body, msg) => {
    try {
      await api.post(`/admin/players/${selected.user_id}/${path}`, body || {});
      toast.success(msg);
      const fresh = await api.get(`/admin/players/${selected.user_id}`);
      setSelected({ ...selected, ...fresh.data.user, status: fresh.data.user.banned ? "banned" : fresh.data.user.suspended ? "suspended" : "active" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async () => {
    if (!selected) return;
    if (!confirm(`Permanently DELETE ${selected.username || selected.email}? This wipes their account, sessions, stats and prize claims. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/players/${selected.user_id}`);
      toast.success("Account deleted");
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const bulkBackfill = async () => {
    setBulkBusy(true);
    try {
      // Dry-run first
      const { data: dry } = await api.post(
        `/admin/ladder/backfill-uncapped-ss?dry_run=true`
      );
      if ((dry.users_affected || 0) === 0) {
        toast.info(
          dry.skipped_already_backfilled > 0
            ? `All players already backfilled (${dry.skipped_already_backfilled}). Nothing to do.`
            : "Nothing to backfill — every player's ledger already matches history."
        );
        return;
      }
      const sample = (dry.results || [])
        .slice(0, 5)
        .map((r) => `  · ${r.user_id.slice(0, 14)}…  +${r.credited.toLocaleString()} SS`)
        .join("\n");
      const ok = window.confirm(
        `Bulk backfill uncapped Ladder SS\n\n` +
        `  Players to credit:  ${dry.users_affected}\n` +
        `  Already backfilled: ${dry.skipped_already_backfilled || 0}\n` +
        `  Total SS to add:    +${(dry.total_ss_credited || 0).toLocaleString()}\n\n` +
        `Top deltas:\n${sample}\n\n` +
        `This credits each player's Season Score + Country War totals. Idempotent.`
      );
      if (!ok) return;
      const { data: applied } = await api.post(
        `/admin/ladder/backfill-uncapped-ss?dry_run=false`
      );
      toast.success(
        `Backfilled ${applied.users_affected} players, +${(applied.total_ss_credited || 0).toLocaleString()} SS total`
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk backfill failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Header />

      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-2 bg-nk-bg border border-nk-line rounded-sm px-3 py-2 flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="text-nk-mute" />
          <input
            data-testid="admin-players-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search username, email or name…"
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>
        <select value={country} onChange={(e) => setCountry(e.target.value)} className="bg-nk-bg border border-nk-line px-3 py-2 rounded-sm text-sm">
          <option value="">All countries</option>
          <option value="NG">Nigeria</option>
          <option value="GH">Ghana</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-nk-bg border border-nk-line px-3 py-2 rounded-sm text-sm">
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="banned">Banned</option>
        </select>
        {isSuperAdmin && (
          <button
            onClick={bulkBackfill}
            disabled={bulkBusy}
            data-testid="admin-players-bulk-backfill-btn"
            title="Credit every player the Ladder SS lost to the (removed) 5,000/day cap. Idempotent."
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-nk-gold/60 text-nk-gold hover:bg-nk-gold/10 rounded-sm text-sm disabled:opacity-50"
          >
            <Lightning size={13} weight="fill" />
            {bulkBusy ? "Running…" : "Bulk backfill Ladder SS"}
          </button>
        )}
      </div>

      <div className="nk-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-nk-line">
            <tr>
              <Th>Player</Th><Th>Country</Th><Th>Rating</Th><Th>Season SS</Th><Th>Status</Th><Th>—</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.user_id} className="border-b border-nk-line/40 hover:bg-nk-surface/40 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <AvatarIcon id={u.avatar} size={22} />
                    <div className="min-w-0">
                      <p className="font-medium flex items-center gap-1.5 truncate">
                        {u.username || u.name} <PremiumBadge active={u.nk_plus_active} size="sm" />
                      </p>
                      <p className="text-[11px] text-nk-dim truncate">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3"><CountryFlag country={u.country} size={14} /></td>
                <td className="px-4 py-3 font-mono">{u.knowledge_rating || 1000}</td>
                <td className="px-4 py-3 font-mono text-nk-accent">{(u.season_score || 0).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <StatusPill status={u.status} />
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setSelected(u)} className="text-xs text-nk-accent hover:underline">View</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-nk-mute text-sm">No players match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <PlayerDrawer
          player={selected}
          onClose={() => setSelected(null)}
          onAct={act}
          onDelete={remove}
        />
      )}
    </div>
  );
}

function Header() {
  return (
    <header className="hidden lg:block">
      <p className="font-mono text-[10px] text-nk-dim uppercase tracking-[0.18em]">Command Center</p>
      <h1 className="font-display text-3xl font-medium tracking-tight">Players</h1>
      <p className="text-sm text-nk-mute mt-2">Search, moderate, and verify champions.</p>
    </header>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-left text-[11px] text-nk-mute font-medium uppercase tracking-wider">{children}</th>;
}

function StatusPill({ status }) {
  if (status === "banned") return <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm bg-nk-bad/15 text-nk-bad">Banned</span>;
  if (status === "suspended") return <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm bg-nk-warn/15 text-nk-warn">Suspended</span>;
  return <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm bg-nk-accent/15 text-nk-accent">Active</span>;
}

function PlayerDrawer({ player, onClose, onAct, onDelete }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-nk-bg border-l border-nk-line p-6 overflow-y-auto animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <AvatarIcon id={player.avatar} size={56} />
            <div>
              <p className="font-display text-xl font-medium flex items-center gap-2">
                {player.username || player.name} <PremiumBadge active={player.nk_plus_active} size="sm" />
              </p>
              <p className="text-xs text-nk-mute mt-0.5">{player.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-nk-mute hover:text-nk-text p-1"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
          <KV k="Country" v={<CountryFlag country={player.country} size={14} />} />
          <KV k="Rating" v={<span className="font-mono">{player.knowledge_rating || 1000}</span>} />
          <KV k="Season SS" v={<span className="font-mono text-nk-accent">{(player.season_score || 0).toLocaleString()}</span>} />
          <KV k="Status" v={<StatusPill status={player.status} />} />
        </div>

        <p className="eyebrow mb-3">Admin actions</p>
        <div className="space-y-2">
          <Link
            to={`/admin/players/${player.user_id}`}
            data-testid="admin-view-profile-link"
            className="w-full inline-flex items-center justify-between px-3 py-2 border border-nk-line rounded-sm text-sm hover:border-nk-accent transition-colors"
          >View full profile <span className="text-nk-mute text-xs">↗</span></Link>
          {player.status === "active" && (
            <ActionBtn onClick={() => onAct("suspend", { days: 7, reason: prompt("Reason?", "") }, "Suspended for 7 days")} Icon={ArrowCounterClockwise} label="Suspend (7 days)" tone="warn" />
          )}
          {player.status === "suspended" && (
            <ActionBtn onClick={() => onAct("unsuspend", null, "Suspension lifted")} Icon={CheckCircle} label="Lift suspension" tone="accent" />
          )}
          {player.status !== "banned" ? (
            <ActionBtn onClick={() => { if (confirm(`Permanently ban ${player.username}? This kicks them out of all live shows.`)) onAct("ban", { reason: prompt("Reason?", "") }, "User banned"); }} Icon={Prohibit} label="Ban account" tone="bad" />
          ) : (
            <ActionBtn onClick={() => onAct("unban", null, "Ban lifted")} Icon={CheckCircle} label="Unban" tone="accent" />
          )}
          <ActionBtn onClick={() => { if (confirm("Reset this player's SEASON score? Knowledge Rating is untouched.")) onAct("reset-stats", null, "Season stats reset"); }} Icon={ArrowCounterClockwise} label="Reset season stats" tone="warn" />
          <ActionBtn onClick={() => onAct("verify-champion", null, "Champion verified")} Icon={Crown} label="Verify as Champion (KYC pass)" tone="gold" />
          <ActionBtn onClick={onDelete} Icon={Trash} label="Delete account permanently" tone="bad" testId="admin-player-delete" />
        </div>
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="nk-card p-3">
      <p className="text-[10px] text-nk-dim uppercase tracking-wider mb-1">{k}</p>
      <div className="text-sm">{v}</div>
    </div>
  );
}

function ActionBtn({ onClick, Icon, label, tone, testId }) {
  const cls = tone === "bad" ? "border-nk-bad text-nk-bad hover:bg-nk-bad/10"
    : tone === "warn" ? "border-nk-warn text-nk-warn hover:bg-nk-warn/10"
    : tone === "gold" ? "border-nk-gold text-nk-gold hover:bg-nk-gold/10"
    : "border-nk-accent text-nk-accent hover:bg-nk-accent/10";
  return (
    <button onClick={onClick} data-testid={testId} className={`w-full inline-flex items-center gap-2 px-3 py-2 border rounded-sm text-sm transition-colors ${cls}`}>
      <Icon size={14} weight="duotone" />
      {label}
    </button>
  );
}
